import { supabase } from '../lib/supabase';
import type {
  ChatbotQuestion,
  FreelancerType,
  ServiceCatalogBenefitTag,
  ServiceCatalogItem,
  ServiceCatalogLead,
  ServiceCatalogLeadStatus,
  ServiceCatalogPageContent,
  chatbot_question_seed,
  freelancer_type_seed,
  service_catalog_benefit_tag_seed,
  service_catalog_item_seed,
} from '@pigeons/shared-service-catalog';

type service_catalog_table_name =
  | 'freelancer_types'
  | 'service_catalog_items'
  | 'service_catalog_benefit_tags'
  | 'chatbot_questions';

type sort_order_update = {
  id: string;
  sort_order: number;
};

const list_rows = async <T>(
  tableName: service_catalog_table_name,
  orderColumn = 'sort_order'
): Promise<T[]> => {
  const { data, error } = await supabase.from(tableName).select('*').order(orderColumn);

  if (error) {
    throw error;
  }

  return (data ?? []) as T[];
};

const create_row = async <T, TPayload extends object>(
  tableName: service_catalog_table_name,
  payload: TPayload
): Promise<T> => {
  const { data, error } = await supabase.from(tableName).insert(payload).select('*').single();

  if (error) {
    throw error;
  }

  return data as T;
};

const update_row = async <T, TPayload extends object>(
  tableName: service_catalog_table_name,
  id: string,
  payload: TPayload
): Promise<T> => {
  const { data, error } = await supabase
    .from(tableName)
    .update(payload)
    .eq('id', id)
    .select('*')
    .single();

  if (error) {
    throw error;
  }

  return data as T;
};

const delete_row = async (tableName: service_catalog_table_name, id: string): Promise<void> => {
  const { error } = await supabase.from(tableName).delete().eq('id', id);

  if (error) {
    throw error;
  }
};

const update_sort_orders = async (
  tableName: service_catalog_table_name,
  updates: sort_order_update[]
): Promise<void> => {
  for (const update of updates) {
    const { error } = await supabase
      .from(tableName)
      .update({ sort_order: update.sort_order })
      .eq('id', update.id);

    if (error) {
      throw error;
    }
  }
};

export const serviceCatalogRepository = {
  listFreelancerTypes: () => list_rows<FreelancerType>('freelancer_types'),
  createFreelancerType: (payload: freelancer_type_seed) =>
    create_row<FreelancerType, freelancer_type_seed>('freelancer_types', payload),
  updateFreelancerType: (id: string, payload: Partial<freelancer_type_seed>) =>
    update_row<FreelancerType, Partial<freelancer_type_seed>>('freelancer_types', id, payload),
  deleteFreelancerType: (id: string) => delete_row('freelancer_types', id),
  reorderFreelancerTypes: (updates: sort_order_update[]) =>
    update_sort_orders('freelancer_types', updates),

  listServiceCatalogItems: () => list_rows<ServiceCatalogItem>('service_catalog_items'),
  createServiceCatalogItem: (payload: service_catalog_item_seed) =>
    create_row<ServiceCatalogItem, service_catalog_item_seed>('service_catalog_items', payload),
  updateServiceCatalogItem: (id: string, payload: Partial<service_catalog_item_seed>) =>
    update_row<ServiceCatalogItem, Partial<service_catalog_item_seed>>(
      'service_catalog_items',
      id,
      payload
    ),
  deleteServiceCatalogItem: (id: string) => delete_row('service_catalog_items', id),
  reorderServiceCatalogItems: (updates: sort_order_update[]) =>
    update_sort_orders('service_catalog_items', updates),

  listFreelancerTypeIdsForItem: async (serviceCatalogItemId: string): Promise<string[]> => {
    const { data, error } = await supabase
      .from('service_catalog_freelancer_types')
      .select('freelancer_type_id')
      .eq('service_catalog_item_id', serviceCatalogItemId);

    if (error) {
      throw error;
    }

    return (data ?? []).map(row => row.freelancer_type_id as string);
  },
  setFreelancerTypeIdsForItem: async (
    serviceCatalogItemId: string,
    freelancerTypeIds: string[]
  ): Promise<void> => {
    const { error } = await supabase.rpc('set_service_catalog_item_freelancer_types', {
      p_service_catalog_item_id: serviceCatalogItemId,
      p_freelancer_type_ids: freelancerTypeIds,
    });

    if (error) {
      throw error;
    }
  },

  listBenefitTags: async (serviceCatalogItemId: string): Promise<ServiceCatalogBenefitTag[]> => {
    const { data, error } = await supabase
      .from('service_catalog_benefit_tags')
      .select('*')
      .eq('service_catalog_item_id', serviceCatalogItemId)
      .order('sort_order');

    if (error) {
      throw error;
    }

    return (data ?? []) as ServiceCatalogBenefitTag[];
  },
  createBenefitTag: (payload: service_catalog_benefit_tag_seed) =>
    create_row<ServiceCatalogBenefitTag, service_catalog_benefit_tag_seed>(
      'service_catalog_benefit_tags',
      payload
    ),
  updateBenefitTag: (id: string, payload: Partial<service_catalog_benefit_tag_seed>) =>
    update_row<ServiceCatalogBenefitTag, Partial<service_catalog_benefit_tag_seed>>(
      'service_catalog_benefit_tags',
      id,
      payload
    ),
  deleteBenefitTag: (id: string) => delete_row('service_catalog_benefit_tags', id),
  reorderBenefitTags: (updates: sort_order_update[]) =>
    update_sort_orders('service_catalog_benefit_tags', updates),

  listChatbotQuestions: async (serviceCatalogItemId: string): Promise<ChatbotQuestion[]> => {
    const { data, error } = await supabase
      .from('chatbot_questions')
      .select('*')
      .eq('service_catalog_item_id', serviceCatalogItemId)
      .order('question_order');

    if (error) {
      throw error;
    }

    return (data ?? []) as ChatbotQuestion[];
  },
  createChatbotQuestion: (payload: chatbot_question_seed) =>
    create_row<ChatbotQuestion, chatbot_question_seed>('chatbot_questions', payload),
  updateChatbotQuestion: (id: string, payload: Partial<chatbot_question_seed>) =>
    update_row<ChatbotQuestion, Partial<chatbot_question_seed>>('chatbot_questions', id, payload),
  /**
   * Deletes the question and clears every goto/show_if that referenced it, in one
   * transaction (see the delete_chatbot_question migration). Returns the item's
   * remaining questions as stored after the delete.
   */
  deleteChatbotQuestion: async (id: string): Promise<ChatbotQuestion[]> => {
    const { data, error } = await supabase.rpc('delete_chatbot_question', {
      p_question_id: id,
    });

    if (error) {
      throw error;
    }

    return (data ?? []) as ChatbotQuestion[];
  },
  reorderChatbotQuestions: async (updates: { id: string; question_order: number }[]) => {
    for (const update of updates) {
      const { error } = await supabase
        .from('chatbot_questions')
        .update({ question_order: update.question_order })
        .eq('id', update.id);

      if (error) {
        throw error;
      }
    }
  },

  listLeads: async (): Promise<ServiceCatalogLead[]> => {
    const { data, error } = await supabase
      .from('leads')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      throw error;
    }

    return (data ?? []) as ServiceCatalogLead[];
  },
  updateLeadStatus: async (
    id: string,
    status: ServiceCatalogLeadStatus
  ): Promise<ServiceCatalogLead> => {
    const { data, error } = await supabase
      .from('leads')
      .update({ status })
      .eq('id', id)
      .select('*')
      .single();

    if (error) {
      throw error;
    }

    return data as ServiceCatalogLead;
  },

  getPageContent: async (): Promise<ServiceCatalogPageContent | null> => {
    const { data, error } = await supabase
      .from('service_catalog_page_content')
      .select('*')
      .maybeSingle();

    if (error) {
      throw error;
    }

    return (data ?? null) as ServiceCatalogPageContent | null;
  },
  updatePageContent: async (
    id: string,
    payload: Partial<Omit<ServiceCatalogPageContent, 'id' | 'updated_at'>>
  ): Promise<ServiceCatalogPageContent> => {
    const { data, error } = await supabase
      .from('service_catalog_page_content')
      .update(payload)
      .eq('id', id)
      .select('*')
      .single();

    if (error) {
      throw error;
    }

    return data as ServiceCatalogPageContent;
  },
};
